import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import classNames from 'classnames'
import type { ReactNode } from 'react'

import AtomTableHeader from '@/components/Atoms/AtomTableHeader/AtomTableHeader'
import { TABLE_EMPTY_TEXT } from '@/constants/ui'
import { getGridTemplateColumns } from '@/utils/table/getGridTemplateColumns'

import cn from './AtomTable.module.scss'

type AtomTableProps<TData> = {
  data: TData[]
  columns: ColumnDef<TData, unknown>[]
  getRowId: (row: TData) => string
  columnWidths?: Partial<Record<string, string>>
  isOrganisationSelected?: boolean
  isStale?: boolean
  emptyContent?: ReactNode
  getRowClassName?: (row: TData) => string | undefined
  ariaLabel?: string
  className?: string
}

function AtomTable<TData>({
  data,
  columns,
  getRowId,
  columnWidths = {},
  isOrganisationSelected = false,
  isStale = false,
  emptyContent,
  getRowClassName,
  ariaLabel,
  className,
}: AtomTableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
  })
  const rows = table.getRowModel().rows

  const gridColumns = getGridTemplateColumns(
    table.getVisibleLeafColumns().map((column) => column.id),
    columnWidths
  )

  if (rows.length === 0) {
    return (
      <div role="status" className={classNames(cn.empty, className)}>
        {emptyContent ?? TABLE_EMPTY_TEXT}
      </div>
    )
  }

  return (
    <div
      className={classNames(
        cn.container,
        isOrganisationSelected && cn.organisationSelected,
        className
      )}
    >
      <div className={cn.table}>
        <div
          role="table"
          aria-label={ariaLabel}
          aria-rowcount={rows.length}
          className={classNames(cn.grid, isStale && cn.stale)}
          style={{ '--grid-columns': gridColumns }}
        >
          <AtomTableHeader table={table} />

          <div role="rowgroup">
            {rows.map((row) => (
              <div
                key={row.id}
                role="row"
                className={classNames(cn.row, getRowClassName?.(row.original))}
              >
                {row.getVisibleCells().map((cell) => (
                  <div key={cell.id} role="cell" className={cn.cell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AtomTable
